/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Chart as ChartJS,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  AverageCompetencies,
  Competencies,
  CompetencyExpectedScore,
  CompetencyLevelPerformance,
  MenteeCompetencyPerformance,
} from '../../types/MentorDashboardStat';
import { useState, useMemo } from 'react';
import { Select } from 'antd';

ChartJS.register(BarElement, Tooltip, Legend, CategoryScale, LinearScale);

interface CompetencyAnalysisChartProps {
  competencyExpectedScore: CompetencyExpectedScore[] | undefined;
  menteePerformance: MenteeCompetencyPerformance[] | undefined;
  competencyLevelPerformance: CompetencyLevelPerformance[] | undefined;
  competencies: Competencies[] | undefined;
  averageCompetencies: AverageCompetencies[] | undefined;
}

const CompetencyAnalysisChart = ({
  competencyExpectedScore,
  menteePerformance,
  competencyLevelPerformance,
  competencies,
  averageCompetencies,
}: CompetencyAnalysisChartProps) => {
  const safeMenteePerformance = menteePerformance || [];
  const safeCompetencies = competencies || [];
  const safeCompetencyExpectedScore = competencyExpectedScore || [];
  const safeCompetencyLevelPerformance = competencyLevelPerformance || [];
  const safeAverageCompetencies = averageCompetencies || [];

  const labels = safeCompetencies.map((c) => c.competencyName);
  const [selectedMentee, setSelectedMentee] = useState<string | null>(
    safeMenteePerformance.length > 0
      ? safeMenteePerformance[0].menteeName
      : null
  );

  const selectedMenteeScores = useMemo(() => {
    if (!selectedMentee) {
      return null;
    }

    return (
      safeMenteePerformance.find(
        (mentee) => mentee.menteeName === selectedMentee
      ) || null
    );
  }, [selectedMentee, safeMenteePerformance, averageCompetencies]);

  const actualScores = useMemo(() => {
    if (selectedMentee === null) {
      return labels.map((competencyName) => {
        const avgCompetency = safeAverageCompetencies.find(
          (competency) => competency.competencyName === competencyName
        );
        return avgCompetency ? avgCompetency.averageScore : 0;
      });
    } else {
      return selectedMenteeScores?.scores.map((score) => score.scores) || [];
    }
  }, [selectedMentee, labels, safeAverageCompetencies, selectedMenteeScores]);

  const expectedScores = useMemo(() => {
    if (!selectedMenteeScores) return labels.map(() => 0);

    return labels.map((competencyName) => {
      const expectedScore = safeCompetencyExpectedScore.find(
        (expecredScore) =>
          expecredScore.tier === selectedMenteeScores.menteeTier &&
          expecredScore.disciplineId ===
            selectedMenteeScores.menteeDiscipline &&
          expecredScore.competencyName === competencyName
      );
      return expectedScore ? expectedScore.expectedScore : 0;
    });
  }, [selectedMenteeScores, labels, safeCompetencyExpectedScore]);

  const data = {
    labels: labels,
    datasets: [
      ...(selectedMentee !== null
        ? [
            {
              label: 'Expected score for a mentee based on tier and discipline',
              data: expectedScores,
              backgroundColor: 'rgba(200, 200, 200, 0.6)',
              borderColor: 'rgba(200, 200, 200, 0.7)',
              stack: 'same-stack',
            },
          ]
        : []),
      {
        label:
          selectedMentee === null
            ? 'Average score across mentees'
            : 'Actual score for the selected mentee',
        data: actualScores,
        backgroundColor: [
          'rgba(2, 247, 219, 1)',
          'rgba(35, 95, 227, 1)',
          'rgba(98, 178, 253, 1)',
          'rgba(192, 132, 252, 1)',
          'rgba(239, 140, 58, 1)',
          'rgba(247, 198, 2, 1)',
          'rgba(32, 188, 91, 1)',
          'rgba(159, 32, 188, 1)',
        ],
        borderColor: [
          'rgba(2, 247, 219, 1)',
          'rgba(35, 95, 227, 1)',
          'rgba(98, 178, 253, 1)',
          'rgba(192, 132, 252, 1)',
          'rgba(239, 140, 58, 1)',
          'rgba(247, 198, 2, 1)',
          'rgba(32, 188, 91, 1)',
          'rgba(159, 32, 188, 1)',
        ],
        borderWidth: 1,
        stack: 'same-stack',
      },
    ],
  };

  const levelLabels = [
    'Level 1 (-)',
    'Level 1',
    'Level 1 (+)',
    'Level 2 (-)',
    'Level 2',
    'Level 2 (+)',
    'Level 3 (-)',
    'Level 3',
    'Level 3 (+)',
    'Level 4 (-)',
    'Level 4',
    'Level 4 (+)',
    'Level 5 (-)',
    'Level 5',
    'Level 5 (+)',
  ];

  const options: any = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          filter: function (item: any) {
            return (
              item.text ===
              'Expected score for a mentee based on tier and discipline'
            );
          },
        },
      },
      tooltip: {
        displayColors: false,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function () {
            return '';
          },
          label: function (context: any) {
            const { dataIndex, datasetIndex } = context;
            const competencyName = labels[dataIndex];
            const competencyPerf = safeCompetencyLevelPerformance.find(
              (perf) => perf.competencyName === competencyName
            );

            const tooltipLines = [];

            if (datasetIndex === 0 && competencyPerf) {
              tooltipLines.push(
                `Highest Rated: ${competencyPerf.highestScoringMentee || 'N/A'}`,
                `Lowest Rated: ${competencyPerf.lowestScoringMentee || 'N/A'}`
              );
            }
            return tooltipLines;
          },
          labelTextColor: function () {
            return 'rgba(97, 97, 97, 1)';
          },
          afterBody: function (context: any) {
            context[0].chart.tooltip.options.backgroundColor = 'white';
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        border: {
          display: false,
          dash: [6, 6],
        },
        stacked: false,
        ticks: {
          stepSize: 1,
          callback: (value: number) => levelLabels[value],
          autoSkip: false,
          maxTicksLimit: 16,
        },
      },
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          font: {
            size: 8.5,
          },
        },
      },
    },
  };

  const menteeOptions = menteePerformance
    ? ['All', ...(menteePerformance ?? []).map((stat) => stat.menteeName)]
    : [];

  const DropdownComponent = ({
    menteeOptions,
    selectedMentee,
    setSelectedMentee,
  }: {
    menteeOptions: string[];
    selectedMentee: string | null;
    setSelectedMentee: React.Dispatch<React.SetStateAction<string | null>>;
  }) => {
    return (
      <div className="mb-4 w-full max-w-[250px] -mt-9">
        <Select
          value={selectedMentee || 'All'}
          onChange={(value) =>
            setSelectedMentee(value === 'All' ? null : value)
          }
          style={{ width: '100%' }}
          placeholder="Select Mentee"
        >
          {menteeOptions.map((menteeName) => (
            <Select.Option key={menteeName} value={menteeName}>
              {menteeName}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  return (
    // mentees drop down
    <div>
      <div className="flex flex-col items-end">
        <DropdownComponent
          menteeOptions={menteeOptions}
          selectedMentee={selectedMentee}
          setSelectedMentee={setSelectedMentee}
        />
      </div>

      <div
        style={{
          position: 'relative',
          height: '350px',
          width: '80%',
          marginTop: '4rem',
        }}
      >
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default CompetencyAnalysisChart;
